import React, { useEffect, useState } from 'react';

import {
  AutoComplete,
  DatePicker,
  Form,
  Input,
  Button,
  Space,
  Select,
  Modal,
  Tooltip,
  Col,
  Row,
  Slider,
  Alert,
  Spin,
  Divider,
} from 'antd';

import dayjs from 'dayjs';
import { withAuth, AuthContextProps } from '../LocalAuthWrapper';
import { MinusCircleOutlined, PlusCircleOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import { Broadcast, CriticalError, CriticalErrorCodes, StateInfo } from '../types';
import { CAPTION_RTMP_API_URL } from '../config';

import Layout from './Layout';
import './BroadcastDetails.css';
import { RTC_MAX_BROADCAST_LENGTH, RTC_MIN_BROADCAST_LENGTH } from '../Constants';
import { HlsVideoPLayer } from '../HlsVideoPlayer';
import { isFeatureEnabled } from '../features';
import RTCCaptionControl from './RTCCaptionControl';
import RTCStreamInfoCopier from './RTCStreamInfoCopier';
import { Link, useHistory } from 'react-router-dom';
import Title from 'antd/es/typography/Title';
import { useGetStreamKeys } from '../hooks/useGetStreamKeys';
import BroadcastStateDisplay from './BroadcastStateDisplay';
import { StateCode } from '../BroadcastStateCodes';
import { isBroadcast } from '../typeguards';

const { confirm } = Modal;

type BroadcastDetailsProps = {
  broadcastId: number;
  userId: string;
} & AuthContextProps;

// eslint-disable-next-line max-lines-per-function, complexity
function BroadCastDetails(props: BroadcastDetailsProps) {
  const history = useHistory();
  const [form] = Form.useForm();
  const tiroRtmpKeyFormVal = Form.useWatch('tiroRtmpKey', form);
  const [broadcast, setBroadcast] = useState<Broadcast | null>(null);
  const [selectedDate, setSelectDate] = useState(dayjs().date());
  const [modificationSuccess, setModificationSuccess] = useState<string>('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState<boolean>(true);
  const [extendingEndTime, setExtendingEndTime] = useState<boolean>(false);
  const streamKeys = useGetStreamKeys(props.userId, props.authCtx.accessToken!, [
    props.authCtx.accessToken,
    props.userId,
  ]);
  const [formStatus, setFormStatus] = useState<'saving' | 'deleting' | 'ending' | 'active'>('active');
  const [criticalError, setCriticalError] = useState<CriticalError>({
    wasThrown: false,
    logMessage: '',
    userMessage: undefined,
    userDetails: undefined,
  });

  const getBroadcast = async (showLoading: boolean) => {
    showLoading && setLoading(true);
    const response = await fetch(`${CAPTION_RTMP_API_URL}/broadcasts/${props.userId}/${props.broadcastId}`, {
      headers: { authorization: `Bearer ${props.authCtx.accessToken}` },
    });

    const handleGetError = (logMsg: string, userMsg: string, userDetails: string, code?: CriticalErrorCodes) => {
      showLoading && setLoading(false);
      setCriticalError({
        wasThrown: true,
        code: code,
        logMessage: logMsg,
        userMessage: userMsg,
        userDetails: userDetails,
      });
    };

    if (!response.ok) {
      handleGetError(
        `Broadcast with id ${props.broadcastId} not found on server. Status code: ${response.status}`,
        'Þessi útsending finnst ekki!',
        'Við erum búin að leita í hverjum krók og kima.',
        'NOT_FOUND',
      );
      return;
    }

    const getErrLogMsg: string = 'Unexpected Broadcast data format received from server.';
    const getErrUserMsg: string = 'Úbbs! Óvænt villa kom upp.';
    const getErrUserDetails: string = 'Við munum gera okkar besta til að fyrirbyggja að þetta gerist aftur.';
    if (!response.headers.get('content-type')?.includes('application/json')) {
      handleGetError(getErrLogMsg, getErrUserMsg, getErrUserDetails, 'INCORRECT_DATA_FORMAT');
      return;
    }

    const json = await response.json();
    if (!isBroadcast(json)) {
      handleGetError(getErrLogMsg, getErrUserMsg, getErrUserDetails, 'INCORRECT_DATA_FORMAT');
      return;
    }

    if (!json.state) {
      // This is a temporary way of handling this until we make the page fully compatible with older stateless versions of Kappi.
      handleGetError(getErrLogMsg, getErrUserMsg, getErrUserDetails, 'STATE_INCOMPATIBLE_SERVER');
      return;
    }

    setBroadcast({ ...json });
    showLoading && setLoading(false);
  };

  useEffect(() => {
    console.log('BROADCAST: Initial fetch');
    getBroadcast(true);
  }, []);

  useEffect(() => {
    // Periodic data fetches (updates).
    console.log('BROADCAST: Setting periodic fetches');
    const updateInterval = window.setInterval(async () => {
      if (criticalError.wasThrown) {
        console.log(
          `BROADCAST: ${
            criticalError.code === 'NOT_FOUND'
              ? 'Not found, '
              : criticalError.code === 'INCORRECT_DATA_FORMAT'
              ? 'Incorrect data format, '
              : criticalError.code === 'STATE_INCOMPATIBLE_SERVER'
              ? 'Current server version does not support broadcast states, '
              : 'Unknown error, '
          }cancelling updates...`,
        );
        clearInterval(updateInterval);
        return;
      }
      if (broadcast) {
        if (terminatedBroadcastStates.includes(broadcast.state.code)) {
          console.log('BROADCAST: Broadcast terminated, cancelling updates...');
          clearInterval(updateInterval);
          return;
        }
      }

      await getBroadcast(false);
      form.setFieldsValue({
        estEndTime: dayjs(broadcast?.est_end_time),
      });
      if (extendingEndTime) {
        setExtendingEndTime(false);
      }
      console.log('Periodic broadcast update...');
    }, 5000);

    return () => {
      if (updateInterval) {
        console.log('BROADCAST: Unmounting and clearing interval');
        clearInterval(updateInterval);
      }
    };
  }, [
    props.broadcastId,
    props.authCtx.accessToken,
    broadcast?.state.code,
    broadcast?.est_end_time,
    criticalError.wasThrown,
    criticalError.code,
  ]);

  // const validateEmails = (emails: string[] | any) => {

  //     let emailsList: string[] = [];
  //     // Split string by comma into an array{
  //     var regex = /[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+/;

  //     emails.forEach((email: string) => {
  //         // Trim whitespaces from email address
  //         if (email === undefined) {
  //             emails.pop(email)
  //         }
  //         // Check email against our regex to determine if email is valid
  //         if (email === "" || !regex.test(email) || email === undefined) {
  //             console.error("Email is not valid or empty.");
  //             return
  //         } else {
  //             email.trim();
  //             emailsList.push(email);
  //         }
  //     })

  //     return emailsList;
  // }

  const createDataJson = (values: any) => {
    let end_time = values.estStartTime.add(values.duration, 'minute').format('YYYY-MM-DD HH:mm').toString() + ':00';
    if (activeOrStreamingBroadcastStates.includes(broadcast?.state.code)) {
      end_time =
        dayjs(values.estEndTime)
          .add(values.extension ? values.extension : 0, 'minute')
          .format('YYYY-MM-DD HH:mm') + ':00';
    }

    try {
      // let emailsList = validateEmails(values.emails);

      return {
        display_name: values.displayName,
        destination: concatDestinationAndRtmpKey(values.destination, values.destinationRtmpKey),
        timeframe: {
          start_time: values.estStartTime.format('YYYY-MM-DD HH:mm').toString() + ':00',
          end_time: end_time,
        },
        fwd_emails: [], // TODO: Use emailsList when the emails field is reintroduced after backend implementation.
        stream_key_public_id: values.tiroRtmpKey,
      };
    } catch (err) {
      console.error(err);
    }
  };

  const onFinish = async (values: any) => {
    setFormStatus('saving');

    try {
      const values = await form.validateFields();
      const data = createDataJson(values);

      if (values.extension && values.extension > 0) {
        setExtendingEndTime(true);
      }

      const requestOptions = {
        method: 'PATCH',
        headers: {
          'Access-Control-Allow-Origin': '*',
          'Content-Type': 'application/json',
          authorization: `Bearer ${props.authCtx.accessToken}`,
        },
        body: JSON.stringify(data),
      };

      const response = await fetch(
        `${CAPTION_RTMP_API_URL}/broadcasts/${props.userId}/${props.broadcastId.toString()}`,
        requestOptions,
      );

      if (!response.ok) {
        const isJson = response.headers.get('content-type')?.includes('application/json');
        if (isJson) {
          const responseMsg = await response.json();
          setError(responseMsg?.error?.message ?? 'Úbbs. Óþekkt villa kom upp!');
        } else {
          setError('Úbbs. Óþekkt villa kom upp!');
        }

        setFormStatus('active');
        return;
      }
    } catch (error) {
      if (error instanceof Object && 'errorFields' in error) {
        console.debug('Validation failed.');
      }
    }

    setFormStatus('active');
    setModificationSuccess('Útsendingu var breytt');
  };

  const onSelect = (date: any) => {
    if (date === dayjs()) {
      setSelectDate(dayjs().date());
      return;
    }
    setSelectDate(date.date());
  };

  const deleteBroadcast = async () => {
    setFormStatus('deleting');

    const requestOptions = {
      method: 'DELETE',
      headers: {
        authorization: `Bearer ${props.authCtx.accessToken}`,
      },
    };

    fetch(`${CAPTION_RTMP_API_URL}/broadcasts/${props.userId}/${props.broadcastId.toString()}`, requestOptions)
      .then(async (response) => {
        if (!response.ok) {
          const error = response.status;
          return Promise.reject(error);
        }
        console.log('Útsendingu hefur verið eytt.');
      })
      .catch((error) => {
        console.error('There was an error!', error);
      });

    setFormStatus('active');

    history.push('/rauntimatextun/broadcasts');
  };

  const endBroadcast = async () => {
    setFormStatus('ending');
    const requestOptions = {
      method: 'POST',
      headers: { authorization: `Bearer ${props.authCtx.accessToken}` },
    };

    try {
      const resp = await fetch(
        `${CAPTION_RTMP_API_URL}/broadcasts/${props.userId}/${props.broadcastId.toString()}/actions/end`,
        requestOptions,
      );
      if (!resp.ok) {
        const data: any = await resp.json();

        setError(data && data.error && data.error.message ? data.error.message : 'Óþekkt villa kom upp.');
        throw new Error(
          data && data.error && data.error.log
            ? data.error.log
            : 'Broadcast end action request failed. Reason unspecified.',
        );
      }
      console.info('Broadcast ended via button.');
      setModificationSuccess('Útsendingu hefur verið lokið.');
    } catch (e) {
      console.error(e);
    }
    setFormStatus('active');
  };

  const disabledDate = (current: any) => {
    return current && current < dayjs().endOf('days').subtract(1, 'days');
  };

  const disabledHours = () => {
    const hours = [];
    const currentHour = dayjs().hour();
    if (selectedDate === dayjs().date()) {
      for (let i = currentHour - 1; i >= 0; i--) {
        hours.push(i);
      }
    }
    return hours;
  };

  const disabledMinutes = (selectedHour: any) => {
    const minutes = [];
    const currentMinute = dayjs().minute();
    if (selectedHour === dayjs().hour()) {
      for (let i = currentMinute; i >= 0; i--) {
        minutes.push(i);
      }
    }
    return minutes;
  };

  const showDangerConfirm = (title: string, content: string, onOk: () => Promise<void>) => {
    return () => {
      confirm({
        title: title,
        icon: <ExclamationCircleFilled />,
        content: content,
        okText: 'Já',
        okType: 'danger',
        cancelText: 'Nei',
        width: '500px',
        onOk() {
          onOk();
        },
        onCancel() {
          console.log('Dangerous action cancelled.');
        },
      });
    };
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log('Failed:', errorInfo);
  };

  const concatDestinationAndRtmpKey = (destination: string, rtmpKey: string) => {
    if (destination.charAt(destination.length - 1) === '/') {
      return destination + rtmpKey;
    }
    return destination + '/' + rtmpKey;
  };

  const separateDestinationAndRtmpKey: (destination?: string) => string[] = (destination?: string) => {
    if (!destination) return ['', ''];

    const rtmpKey: string = destination?.split('/').slice(-1)[0];
    const destinationWithoutRtmpKey: string = destination.substring(0, destination.length - rtmpKey.length);

    return [destinationWithoutRtmpKey, rtmpKey];
  };

  const displayName = broadcast?.display_name;
  const [destination, destinationRtmpKey] = separateDestinationAndRtmpKey(broadcast?.destination);
  const estStartTime = broadcast?.est_start_time;
  const estEndTime = broadcast?.est_end_time;
  const fwdEmails = broadcast?.fwd_emails;
  const tiroRtmpKey = broadcast?.stream_key_public_id;
  const state: StateInfo | undefined = broadcast?.state;
  const duration = dayjs(estEndTime).diff(dayjs(estStartTime), 'minute');

  const nonTerminatedBroadcastStates: (StateCode | undefined)[] = [
    StateCode.scheduled,
    StateCode.active,
    StateCode.streaming,
    StateCode.overstreaming,
  ];
  const streamingBroadcastStates: (StateCode | undefined)[] = [StateCode.streaming, StateCode.overstreaming];
  const activeOrStreamingBroadcastStates: (StateCode | undefined)[] = [StateCode.active, ...streamingBroadcastStates];
  const terminatedBroadcastStates: (StateCode | undefined)[] = [
    StateCode.finished,
    StateCode.cancelled,
    StateCode.error,
  ];

  const autoCompleteDestinations: { value: string }[] = [
    { value: 'rtmps://live-api-s.facebook.com:443/rtmp' },
    { value: 'rtmps://rtmp-global.cloud.vimeo.com:443/live' },
    { value: 'rtmp://a.rtmp.youtube.com/live2' },
  ];

  if (criticalError.wasThrown) {
    console.error(`Critical error: ${criticalError.logMessage} - Code: ${criticalError.code}`);
    return (
      <Layout>
        <div className='container-wrapper'>
          <div className='details-container'>
            <Space direction='vertical'>
              <Title>
                {criticalError.userMessage ? criticalError.userMessage : 'Úbbs, villa! Þetta eru nú meiri leiðindin.'}
              </Title>
              <Title level={2}>
                {criticalError.userDetails ? criticalError.userDetails : 'Okkur þykir þetta afskaplega leiðinlegt.'}
              </Title>
            </Space>
            <Title level={3}>
              Afsakaðu þetta. Ef þetta er að gerast ítrekað skaltu hiklaust hafa samband við
              <a href='mailto:tiro@tiro.is'> tiro@tiro.is</a>.
            </Title>
            <Link to={'/rauntimatextun/broadcasts'}>
              <Button id='broadcast-details-back-btn' type='default'>
                Til baka
              </Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const displayPreview: boolean | null =
    isFeatureEnabled('REAL_TIME_CAPTIONING_PREVIEW') && broadcast && nonTerminatedBroadcastStates.includes(state?.code);

  console.info(`Broadcast state description: ${state?.description_log}`);
  return (
    <Layout>
      <div className='container-wrapper'>
        <div className='details-container'>
          <div className='details-container-banner'>
            <div className='details-container-banner-info'>
              <Title italic>{displayName}</Title>
              {displayPreview && (
                <BroadcastStateDisplay
                  stateInfo={state}
                  broadcastTimeframe={{ estStartTime: estStartTime!, estEndTime: estEndTime! }}
                />
              )}
            </div>
            {displayPreview ? (
              <div className='details-container-banner-preview'>
                <Title>Frálag textunarþjónustu</Title>
                <HlsVideoPLayer
                  style={{
                    maxWidth: '600px',
                    width: '100%',
                    objectFit: 'fill',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                  }}
                  src={
                    streamingBroadcastStates.includes(broadcast?.state.code)
                      ? `${CAPTION_RTMP_API_URL.replace(
                          '/api',
                          '/hls/preview_',
                        )}${broadcast?.stream_key_public_id}.m3u8`
                      : ''
                  }
                  broadcastActive={activeOrStreamingBroadcastStates.includes(state?.code)}
                />
              </div>
            ) : (
              <BroadcastStateDisplay
                stateInfo={state}
                broadcastTimeframe={{ estStartTime: estStartTime!, estEndTime: estEndTime! }}
              />
            )}
          </div>
          {!!modificationSuccess && (
            <Alert
              closable
              onClose={() => setModificationSuccess('')}
              message='Aðgerð tókst'
              description={modificationSuccess}
              type='success'
              showIcon
            />
          )}
          {!!error && (
            <Alert
              closable
              onClose={() => setError('')}
              message='Það kom upp villa!'
              description={`${error}`}
              type='error'
              showIcon
            />
          )}
          <div>
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
                <Spin size='large' />
              </div>
            ) : (
              <React.Fragment>
                <Form
                  form={form}
                  onFinish={onFinish}
                  onFinishFailed={onFinishFailed}
                  id='rtc-create-form'
                  labelCol={{ span: 7 }}
                  wrapperCol={{ span: 11 }}
                  style={{ width: '100%', margin: '0 auto' }}
                  initialValues={{
                    remember: true,
                    displayName: displayName,
                    destination: destination,
                    destinationRtmpKey: destinationRtmpKey,
                    estStartTime: dayjs(estStartTime),
                    estEndTime: dayjs(estEndTime),
                    duration: duration,
                    emails: fwdEmails,
                    tiroRtmpKey: tiroRtmpKey,
                  }}
                >
                  <Divider orientation='left'>Grunnupplýsingar</Divider>
                  <Form.Item
                    name='displayName'
                    label='Heiti'
                    rules={[
                      {
                        type: 'string',
                        required: true,
                        message: 'Vantar nafn á útsendingu!',
                        whitespace: true,
                      },
                    ]}
                  >
                    <Input disabled={['ERROR', 'CANCELLED'].includes(state?.code!)} />
                  </Form.Item>
                  <Form.Item
                    name='estStartTime'
                    label='Upphafstímasetning'
                    rules={[{ required: true, message: 'Vantar upphafstímasetningu!' }]}
                  >
                    <DatePicker
                      minuteStep={5}
                      onSelect={onSelect}
                      showTime={{ format: 'HH:mm' }}
                      format='YYYY-MM-DD HH:mm'
                      style={{ width: '100%' }}
                      disabledDate={disabledDate}
                      disabledHours={disabledHours}
                      disabledMinutes={disabledMinutes}
                      disabled={state?.code !== 'SCHEDULED'}
                      showNow={false}
                    />
                  </Form.Item>
                  {activeOrStreamingBroadcastStates.includes(state?.code) ? (
                    <Form.Item name='estEndTime' label='Útsendingu lýkur' colon={false}>
                      <DatePicker
                        disabled={true}
                        showTime={{ format: 'HH:mm' }}
                        format='YYYY-MM-DD HH:mm'
                        style={{ width: '100%' }}
                        suffixIcon={extendingEndTime ? <Spin size='small' /> : null}
                      />
                    </Form.Item>
                  ) : null}
                  {activeOrStreamingBroadcastStates.includes(state?.code) ? (
                    <Form.Item name='extension' label='Framlengja útsendingu'>
                      <Slider
                        min={0}
                        max={dayjs(estStartTime)
                          .add(RTC_MAX_BROADCAST_LENGTH, 'minute')
                          .diff(dayjs(estEndTime), 'minute')}
                        value={0}
                        tooltip={{
                          formatter: (val: any) =>
                            duration !== RTC_MAX_BROADCAST_LENGTH
                              ? `${val} mínút${val > 1 ? 'ur' : 'a'} - Útsendingu lýkur: ${dayjs(estEndTime)
                                  .add(val, 'minute')
                                  .format('YYYY-MM-DD HH:mm')}`
                              : 'Útsending er í hámarkslengd',
                        }}
                        disabled={duration === RTC_MAX_BROADCAST_LENGTH}
                      />
                    </Form.Item>
                  ) : (
                    <Form.Item name='duration' label='Tímalengd' rules={[{ required: true }]}>
                      <Slider
                        min={RTC_MIN_BROADCAST_LENGTH}
                        max={RTC_MAX_BROADCAST_LENGTH}
                        value={RTC_MIN_BROADCAST_LENGTH}
                        tooltip={{
                          formatter: (val: any) =>
                            `${val} mínút${val > 1 ? 'ur' : 'a'} - Útsendingu lýkur: ${dayjs(estStartTime)
                              .add(val, 'minute')
                              .format('YYYY-MM-DD HH:mm')}`,
                        }}
                        disabled={state?.code !== 'SCHEDULED'}
                      />
                    </Form.Item>
                  )}
                  <Form.List name='emails'>
                    {
                      /* The emails field is temporily removed until the forwarding/sharing implementation has been implemented in the backend. */
                      /* TODO: Make first field visible for input without needing to press "Add" button */
                      (fields, { add, remove }, { errors }) => {
                        if (false /* eslint-disable-line no-constant-condition */) {
                          return (
                            <>
                              {fields.map((field, index) => (
                                <Form.Item
                                  // {...formItemLayout}
                                  label='Viðtakandi'
                                  required={false}
                                  key={field.key}
                                >
                                  <Row>
                                    <Col>
                                      <Form.Item
                                        name={field.name}
                                        validateTrigger={['onChange', 'onBlur']}
                                        rules={[
                                          {
                                            required: true,
                                            message: 'Vinsamlegast bætið við netfangi eða eyðið reitnum.',
                                          },
                                        ]}
                                        noStyle
                                      >
                                        <Input
                                          placeholder='Netfang'
                                          style={{ width: '95%' }}
                                          disabled={terminatedBroadcastStates.includes(state?.code)}
                                        />
                                      </Form.Item>
                                    </Col>
                                    <Col>
                                      <Tooltip title='Fjarlægja viðtakanda'>
                                        <Button
                                          onClick={() => remove(field.name)}
                                          shape='circle'
                                          type='dashed'
                                          icon={<MinusCircleOutlined className='dynamic-delete-button' />}
                                          disabled={terminatedBroadcastStates.includes(state?.code)}
                                        />
                                      </Tooltip>
                                    </Col>
                                  </Row>
                                </Form.Item>
                              ))}
                              <Form.Item>
                                <Button
                                  type='dashed'
                                  onClick={() => add()}
                                  disabled={terminatedBroadcastStates.includes(state?.code)}
                                  icon={<PlusCircleOutlined />}
                                >
                                  Bæta við viðtakanda
                                </Button>
                                <Form.ErrorList errors={errors} />
                              </Form.Item>
                            </>
                          );
                        }
                      }
                    }
                  </Form.List>
                  <Divider orientation='left'>Textunarþjónusta</Divider>
                  <Form.Item label='Slóð' colon={false}>
                    <RTCStreamInfoCopier
                      streamKeyVal={streamKeys.find((s) => s.option.value === tiroRtmpKeyFormVal)?.keyVal || ''}
                    />
                  </Form.Item>
                  <Form.Item label='Streymislykill'>
                    <Form.Item
                      name='tiroRtmpKey'
                      rules={[{ required: true, message: 'Vantar að velja streymislykil' }]}
                    >
                      <Select
                        placeholder='Vinsamlegast veljið streymislykil'
                        options={[...streamKeys.map((s) => s.option)]}
                        disabled={state?.code !== 'SCHEDULED'}
                      ></Select>
                    </Form.Item>
                  </Form.Item>
                  {
                    /* Caption control is only visible for broadcasts which are active or streaming. */
                    activeOrStreamingBroadcastStates.includes(state?.code) ? (
                      <RTCCaptionControl streamKeyPublicId={tiroRtmpKeyFormVal} userId={props.userId} />
                    ) : null
                  }
                  <Divider orientation='left'>Áfangastaður</Divider>
                  <Form.Item
                    name='destination'
                    label='Slóð'
                    rules={[
                      {
                        required: true,
                        message: 'Vantar gildan áfangastað',
                      },
                      {
                        pattern:
                          /^rtmps?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)$/,
                        message: 'Vantar áfangastað með gildan rtmp(s)://<hlekk>!',
                      },
                    ]}
                  >
                    <AutoComplete
                      placeholder='rtmp(s)://...'
                      options={autoCompleteDestinations}
                      filterOption={(inputValue, option) => option!.value.includes(inputValue)}
                      disabled={state?.code !== 'SCHEDULED'}
                    />
                  </Form.Item>
                  <Form.Item
                    name='destinationRtmpKey'
                    label='Streymislykill'
                    rules={[
                      {
                        required: true,
                        message: 'Vantar gildan RTMP-lykil',
                      },
                      {
                        pattern: /\S*/gm,
                        message: 'Vantar RTMP-lykil',
                      },
                    ]}
                  >
                    <Input disabled={state?.code !== 'SCHEDULED'} />
                  </Form.Item>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Space size='small'>
                      <Button
                        type='primary'
                        htmlType='submit'
                        loading={formStatus === 'saving'}
                        disabled={formStatus !== 'active' || ['CANCELLED', 'ERROR'].includes(state?.code!)}
                      >
                        Vista
                      </Button>
                      <Button
                        type='primary'
                        danger
                        loading={formStatus === 'deleting'}
                        disabled={formStatus !== 'active' || !['SCHEDULED', 'CANCELLED'].includes(state?.code!)}
                        onClick={showDangerConfirm(
                          `Þú ert að fara að eyða útsendingu: ${displayName}`,
                          'Ertu viss um að þú viljir eyða þessari útsendingu? Þessa aðgerð er ekki hægt að afturkalla!',
                          deleteBroadcast,
                        )}
                      >
                        Eyða
                      </Button>
                      <Tooltip
                        title={
                          streamingBroadcastStates.includes(state?.code!)
                            ? 'Vinsamlega stöðvið streymi áður en útsendingu er lokið.'
                            : state?.code === 'SCHEDULED'
                            ? 'Útsending er ekki hafin.'
                            : state?.code === 'ACTIVE'
                            ? 'Ljúka útsendingu núna.'
                            : state?.code === 'FINISHED'
                            ? 'Útsendingu er þegar lokið.'
                            : 'Ekki er hægt að ljúka þessari útsendingu að svo stöddu.'
                        }
                      >
                        <Button
                          danger
                          loading={formStatus === 'ending'}
                          disabled={
                            formStatus !== 'active' ||
                            streamingBroadcastStates.includes(state?.code) ||
                            ['SCHEDULED', ...terminatedBroadcastStates].includes(state?.code!)
                          }
                          onClick={showDangerConfirm(
                            `Þú ert að fara að ljúka útsendingu fyrir áætlaðan lokatíma: ${displayName}`,
                            'Ertu viss um að þú viljir ljúka þessari útsendingu? Þessa aðgerð er ekki hægt að afturkalla!',
                            endBroadcast,
                          )}
                        >
                          Ljúka
                        </Button>
                      </Tooltip>
                    </Space>
                  </div>
                </Form>
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
                  <Link to={'/rauntimatextun/broadcasts'}>
                    <Button id='broadcast-details-back-btn' type='default'>
                      Til baka
                    </Button>
                  </Link>
                </div>
              </React.Fragment>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default withAuth(BroadCastDetails);
